import merge from 'deepmerge';
import CodeMirror from 'codemirror';
import Monkberry from 'monkberry';
import directives from 'monkberry-directives';
import 'monkberry-events';
import ExecutableCodeTemplate from './executable-fragment.monk';
import WebDemoApi from '../webdemo-api';
import SchemaExecutableFragment from './schema-executable-fragment';

import {
  isJavaRelated,
  isJsRelated,
  isWasmRelated,
  TargetPlatforms,
} from '../utils/platforms';
import JsExecutor from '../js-executor';

import {
  escapeRegExp,
  MARK_PLACEHOLDER_CLOSE,
  MARK_PLACEHOLDER_OPEN,
  SAMPLE_END,
  SAMPLE_START,
  unEscapeString,
} from '../utils/escape';

import { countLines, THEMES } from '../utils';
import debounce from 'debounce';
import { processErrors, parseOutputObject } from '../view/output-view';
import { fetch } from 'whatwg-fetch';
import { API_URLS } from '../config';
import { setupAutocomplete } from './autocomplete';
const KEY_CODES = {
  R: 82,
  F9: 120,
  ENTER: 13,
};
const DEBOUNCE_TIME = 3000;
const MAC = 'Mac';

const SELECTORS = {
  JS_CODE_OUTPUT_EXECUTOR: '.js-code-output-executor',
  FOLD_BUTTON: '.fold-button',
  UNMODIFIABLE_LINE_DARK: 'unmodifiable-line-dark',
  UNMODIFIABLE_LINE: 'unmodifiable-line',
  MARK_PLACEHOLDER: 'markPlaceholder',
  MARK_PLACEHOLDER_START: 'markPlaceholder-start',
  MARK_PLACEHOLDER_END: 'markPlaceholder-end',
  GUTTER: 'gutter',
  ERROR: 'ERROR',
  FOLD_GUTTER: 'CodeMirror-foldgutter',
  ERROR_GUTTER: 'ERRORgutter',
  ERROR_AND_WARNING_GUTTER: 'errors-and-warnings-gutter',
  BACKGROUND: 'background',
  LABEL: 'label',
};

export default class ExecutableFragment extends ExecutableCodeTemplate {
  static render(element, options = {}) {
    const instance = Monkberry.render(ExecutableFragment, element, {
      directives: directives,
    });

    instance.arrayClasses = [];
    instance.initialized = false;
    instance.state = {
      theme: '',
      code: '',
      foldButtonHover: false,
      folded: true,
      output: null,
      sqlOutput: null,
      waitingForOutput: false,
      exception: null,
      activeTab: 'code',
      hasSchema: false,
      databaseSchema: '',
      annotation: '',
      slug: '',
      page: '',
      // Needed so that we don't render initial-output text beyond one code execution
      executedAtLeastOnce: false,
      // Track whether the user has actually interacted with the editor
      changedAtLeastOnce: false,
    };
    instance.codemirror = new CodeMirror();
    instance.schemaFragment = null;

    instance.on('click', SELECTORS.FOLD_BUTTON, () => {
      // If allowEditOutside is enabled, first click should unfold and permanently hide the fold button
      if (instance.state && instance.state.allowEditOutside) {
        if (instance.state.folded) {
          instance.update({ folded: false, isFoldedButton: false });
        }
        return;
      }
      // Default behavior: toggle folded state
      instance.update({ folded: !instance.state.folded });
    });

    instance.onTabClick = function(tab) {
      instance.update({ activeTab: tab });

      // Refresh schema editor when switching to it (CodeMirror needs this when becoming visible)
      if (tab === 'schema' && instance.schemaFragment && instance.schemaFragment.codemirror) {
        setTimeout(() => {
          instance.schemaFragment.codemirror.refresh();
          instance.schemaFragment.codemirror.focus();
        }, 0);
      } else if (tab === 'code' && instance.codemirror) {
        setTimeout(() => {
          instance.codemirror.refresh();
          instance.codemirror.focus();
        }, 0);
      }
    };

    instance.on('keyup', (event) => {
      if (window.navigator.appVersion.indexOf(MAC) !== -1) {
        if (event.keyCode === KEY_CODES.R && event.ctrlKey) {
          instance.execute();
        }
      } else {
        if (event.keyCode === KEY_CODES.F9 && event.ctrlKey) {
          instance.execute();
        }
      }
    });

    const events = options.eventFunctions;
    if (events && events.getInstance) events.getInstance(instance);

    return instance;
  }

  get isShouldBeFolded() {
    return this.prefix.trim() !== '' || this.suffix.trim() !== '';
  }

  update(state) {
    let sample;
    let hasMarkers = false;
    let platform = state.targetPlatform;
    if (
      (state.compilerVersion && isJsRelated(platform)) ||
      isWasmRelated(platform)
    ) {
      this.jsExecutor = new JsExecutor(state.compilerVersion);
    }

    if (!state.shorterHeight) {
      this.codemirror.display.wrapper.style.maxHeight = '';
    }

    if (state.code) {
      let code = state.code;
      if (
        state.from &&
        state.to &&
        state.to >= state.from &&
        state.from > 0 &&
        state.to > 0
      ) {
        let codeLines = code.split('\n');
        codeLines.splice(state.from - 1, 0, SAMPLE_START);
        codeLines.splice(state.to + 1, 0, SAMPLE_END);
        code = codeLines.join('\n');
      }
      const startIndex = code.indexOf(SAMPLE_START);
      const endIndex = code.indexOf(SAMPLE_END);
      hasMarkers = !state.noneMarkers && startIndex > -1 && endIndex > -1;

      this.prefix = '';
      this.suffix = '';
      sample = code;

      this.canAddImport = true;

      if (hasMarkers) {
        this.prefix = code.substring(0, startIndex);
        this.canAddImport = this.prefixEmptyOrContainsOnlyImports();
        this.suffix = code.substring(endIndex + SAMPLE_END.length);
        sample = code.substring(
          startIndex + SAMPLE_START.length + 1,
          endIndex - 1,
        );
      }

      if (this.suffix.endsWith('\n')) {
        this.suffix = this.suffix.substr(0, this.suffix.length - 1);
      }
    } else {
      if (this.state.folded) {
        sample = this.codemirror.getValue();
      } else {
        let editorValue = this.codemirror.getValue();
        sample = editorValue.substring(
          this.prefix.length,
          editorValue.length - this.suffix.length,
        );
      }
    }

    this.state = merge.all([
      this.state,
      state,
      {
        isShouldBeFolded: this.isShouldBeFolded && state.isFoldedButton,
      },
    ]);

    super.update(this.state);

    if (!this.initialized) {
      this.initializeCodeMirror(state);
      this.initialized = true;
    } else {
      this.showDiagnostics(state.errors);
      if (state.folded === undefined) {
        return;
      }
    }

    // Initialize schema fragment when unfolded and hasSchema is true
    if (!this.state.folded && this.state.hasSchema && !this.schemaFragment) {
      this.initializeSchemaFragment();
    }

    // Clean up schema fragment when folded
    if (this.state.folded && this.schemaFragment) {
      this.schemaFragment.destroy();
      this.schemaFragment = null;
    }

    if (this.state.folded) {
      this.codemirror.setOption('lineNumbers', state.lines && !hasMarkers);
      this.codemirror.setValue(sample);
      this.markPlaceHolders();
    } else {
      this.codemirror.setOption('lineNumbers', true);
      this.codemirror.setValue(this.prefix + sample + this.suffix);
      if (!this.state.allowEditOutside) {
        this.codemirror.markText(
          { line: 0, ch: 0 },
          { line: countLines(this.prefix), ch: 0 },
          {
            readOnly: true,
            inclusiveLeft: true,
            inclusiveRight: false,
          },
        );
        this.codemirror.markText(
          {
            line: this.codemirror.lineCount() - countLines(this.suffix) - 1,
            ch: null,
          },
          { line: this.codemirror.lineCount() - 1, ch: null },
          {
            readOnly: true,
            inclusiveLeft: false,
            inclusiveRight: true,
          },
        );
        let readOnlyLineClass =
          this.state.theme === THEMES.DARCULA
            ? SELECTORS.UNMODIFIABLE_LINE_DARK
            : SELECTORS.UNMODIFIABLE_LINE;
        this.codemirror.operation(() => {
          for (let i = 0; i < countLines(this.prefix); i++) {
            this.codemirror.addLineClass(
              i,
              SELECTORS.BACKGROUND,
              readOnlyLineClass,
            );
          }

          for (
            let i = this.codemirror.lineCount() - countLines(this.suffix);
            i < this.codemirror.lineCount();
            i++
          ) {
            this.codemirror.addLineClass(
              i,
              SELECTORS.BACKGROUND,
              readOnlyLineClass,
            );
          }
        });
      }
    }
    if (this.state.autoIndent || (this.prefix && this.suffix)) {
      for (let i = 0; i < this.codemirror.lineCount(); i++) {
        this.codemirror.indentLine(i);
      }
    }

    const shorterHeight = this.state.shorterHeight;

    if (shorterHeight) {
      const wrapper = this.codemirror.display.wrapper;

      if (wrapper.getBoundingClientRect().height + 10 > shorterHeight) {
        this.codemirror.display.wrapper.style.maxHeight = `${shorterHeight}px`;
      } else {
        super.update({ shorterHeight: 0 });
      }
    }
  }

  markPlaceHolders() {
    let taskRanges = this.getTaskRanges();
    this.codemirror.setValue(
      this.codemirror
        .getValue()
        .replace(new RegExp(escapeRegExp(MARK_PLACEHOLDER_OPEN), 'g'), '')
        .replace(new RegExp(escapeRegExp(MARK_PLACEHOLDER_CLOSE), 'g'), ''),
    );

    taskRanges.forEach((task) => {
      this.codemirror.markText(
        { line: task.line, ch: task.ch },
        { line: task.line, ch: task.ch + task.length },
        {
          className: SELECTORS.MARK_PLACEHOLDER,
          startStyle: SELECTORS.MARK_PLACEHOLDER_START,
          endStyle: SELECTORS.MARK_PLACEHOLDER_END,
          handleMouseEvents: true,
        },
      );
    });
  }

  getTaskRanges() {
    let textRanges = [];
    let fileContentLines = this.codemirror.getValue().split('\n');
    for (let i = 0; i < fileContentLines.length; i++) {
      let line = fileContentLines[i];
      while (line.includes(MARK_PLACEHOLDER_OPEN)) {
        let markPlaceHolderStart = line.indexOf(MARK_PLACEHOLDER_OPEN);
        line = line.replace(MARK_PLACEHOLDER_OPEN, '');
        let markPlaceHolderEnd = line.indexOf(MARK_PLACEHOLDER_CLOSE);
        line = line.replace(MARK_PLACEHOLDER_CLOSE, '');
        textRanges.push({
          line: i,
          ch: markPlaceHolderStart,
          length: markPlaceHolderEnd - markPlaceHolderStart,
        });
      }
    }
    return textRanges;
  }

  onFoldButtonMouseEnter() {
    if (!this.state.foldButtonHover) {
      this.update({ foldButtonHover: true });
    }
  }

  onFoldButtonMouseLeave() {
    if (this.state.foldButtonHover) {
      this.update({ foldButtonHover: false });
    }
  }

  onConsoleCloseButtonEnter() {
    const { jsLibs, onCloseConsole, targetPlatform, compilerVersion } = this.state;
    // creates a new iframe and removes the old one, thereby stops execution of any running script
    if (isJsRelated(targetPlatform) || isWasmRelated(targetPlatform))
      this.jsExecutor.reloadIframeScripts(
        jsLibs,
        this.getNodeForMountIframe(),
        targetPlatform,
        compilerVersion,
      );
    this.update({ output: '', openConsole: false, exception: null });
    if (onCloseConsole) onCloseConsole();
  }

  onExceptionClick(fileName, line) {
    this.codemirror.setCursor(line - 1, 0);
    this.codemirror.focus();
  }

  onFoldButtonClick() {
    this.update({ shorterHeight: 0 });
  }

  onShorterClick() {
    this.update({ shorterHeight: 0 });
  }

  execute() {
    const {
      onOpenConsole,
      targetPlatform,
      waitingForOutput,
      compilerVersion,
      onRun,
      onError,
      args,
      theme,
      hiddenDependencies,
      onTestPassed,
      onTestFailed,
      onCloseConsole,
      jsLibs,
      outputHeight,
      getJsCode,
    } = this.state;
    if (waitingForOutput) {
      return;
    }
    this.update({
      shorterHeight: 0,
      waitingForOutput: true,
      openConsole: false,
      executedAtLeastOnce: true,
    });
    if (onOpenConsole) onOpenConsole(); //open when waitingForOutput=true
    if (onRun) onRun();
    if (isJavaRelated(targetPlatform)) {
      WebDemoApi.executeKotlinCode(
        this.getCode(),
        compilerVersion,
        targetPlatform,
        args,
        theme,
        hiddenDependencies,
        onTestPassed,
        onTestFailed,
        this.state.databaseSchema,
        this.state.annotation,
        this.state.slug,
        this.state.page,
      ).then(
        (state) => {
          state.waitingForOutput = false;
          state.sqlOutput = state.backendOutput.sqlOutput;
          state.output = state.backendOutput.standardOutput;
          
          if (state.output || state.exception) {
            state.openConsole = true;
          } else {
            if (onCloseConsole) onCloseConsole();
          }
          if ((state.errors.length > 0 || state.exception) && onError)
            onError();
          this.update(state);
        },
        (error) => {
          if (onError) onError();
          this.update({
            waitingForOutput: false,
            output: processErrors([
              {
                severity: 'ERROR',
                message: error.message,
              },
            ]),
            openConsole: true,
            exception: null,
          });
        },
      );
    } else {
      this.jsExecutor.reloadIframeScripts(
        jsLibs,
        this.getNodeForMountIframe(),
        targetPlatform,
        compilerVersion,
      );
      const additionalRequests = [];
      if (targetPlatform === TargetPlatforms.COMPOSE_WASM) {
        if (this.jsExecutor.stdlibExports) {
          additionalRequests.push(this.jsExecutor.stdlibExports);
        }
      }

      Promise.all([
        WebDemoApi.translateKotlinToJs(
          this.getCode(),
          compilerVersion,
          targetPlatform,
          args,
          hiddenDependencies,
          this.state.databaseSchema,
        ),
        ...additionalRequests,
      ]).then(
        ([state, ...additionalRequestsResults]) => {
          state.waitingForOutput = false;
          const jsCode = state.jsCode;
          const wasm = state.wasm;
          delete state.jsCode;
          if (getJsCode) getJsCode(jsCode);
          let errors = state.errors.filter(
            (error) => error.severity === 'ERROR',
          );
          if (errors.length > 0) {
            if (onError) onError();
            state.output = processErrors(errors);
            state.openConsole = true;
            state.exception = null;
            this.update(state);
          } else {
            this.jsExecutor
              .executeJsCode(
                jsCode,
                wasm,
                jsLibs,
                targetPlatform,
                outputHeight,
                theme,
                onError,
                additionalRequestsResults,
              )
              .then((output) => {
                const originState = state.openConsole;

                if (
                  targetPlatform === TargetPlatforms.CANVAS ||
                  targetPlatform === TargetPlatforms.COMPOSE_WASM
                ) {
                  state.openConsole = true;
                }

                if (output) {
                  state.openConsole = true;
                  state.output = output;
                } else {
                  state.output = '';
                  if (onCloseConsole) onCloseConsole();
                }

                if (
                  onOpenConsole &&
                  originState !== state.openConsole &&
                  state.openConsole === true
                )
                  onOpenConsole();

                state.exception = null;
                this.update(state);
                /*
                if (targetPlatform === TargetPlatforms.SWIFT_EXPORT) {
                  const code = this.nodes[0]
                    .querySelector(SELECTORS.JS_CODE_OUTPUT_EXECUTOR)
                    .querySelector('.result-code');

                  if (code) {
                    CodeMirror(
                      (elt) => code.parentNode.replaceChild(elt, code),
                      {
                        mode: 'swift',
                        readOnly: 'nocursor',
                        scrollbarStyle: 'native',
                        theme: this.state.theme,
                        value: code.innerText,
                      },
                    );
                  }
                }
                */
              });
          }
        },
        (error) => {
          if (onError) onError();
          this.update({
            waitingForOutput: false,
            output: processErrors([
              {
                severity: 'ERROR',
                message: error.message,
              },
            ]),
            openConsole: true,
            exception: null,
          });
        },
      );
    }
  }

  /**
   * @param {TargetPlatform} platform
   * @return {HTMLElement}
   */
  getNodeForMountIframe() {
    return this.nodes[0].querySelector(SELECTORS.JS_CODE_OUTPUT_EXECUTOR);
  }

  getCode() {
    if (this.state.folded) {
      return this.prefix + this.codemirror.getValue() + this.suffix;
    } else {
      return this.codemirror.getValue();
    }
  }

  prefixEmptyOrContainsOnlyImports() {
    return this.prefix
      .split('\n')
      .every((line) => /^\s*(package |import |$)/.test(line));
  }

  initializeSchemaFragment() {
    const schemaContainer = this.nodes[0].querySelector('.schema-container');
    if (schemaContainer && !this.schemaFragment) {
      this.schemaFragment = SchemaExecutableFragment.render(schemaContainer);
      this.schemaFragment.update({
        code: this.state.databaseSchema || '',
        theme: this.state.theme,
        mode: this.state.mode,
        indent: this.state.indent,
        scrollbarStyle: this.state.scrollbarStyle,
        onChange: (value) => {
          this.state.databaseSchema = value;
        }
      });

      // Refresh after creation to ensure proper rendering
      setTimeout(() => {
        if (this.schemaFragment && this.schemaFragment.codemirror) {
          this.schemaFragment.codemirror.refresh();
        }
      }, 100);
    }
  }

  recalculatePosition(position) {
    const newPosition = {
      line: position.line,
      ch: position.ch,
    };
    if (!this.state.folded) {
      return newPosition;
    }

    let linesInPrefix = (this.prefix.match(/\n/g) || []).length;
    newPosition.line = position.line - linesInPrefix;
    if (newPosition.line < 0) {
      newPosition.line = 0;
      newPosition.ch = 0;
    } else if (newPosition.line >= this.codemirror.lineCount()) {
      newPosition.line = this.codemirror.lineCount() - 1;
      newPosition.ch = null;
    }
    return newPosition;
  }

  showDiagnostics(diagnostics) {
    this.removeStyles();
    if (diagnostics === undefined) {
      return;
    }
    diagnostics.forEach((diagnostic) => {
      const interval = diagnostic.interval;
      if (interval == undefined) {
        return;
      }
      interval.start = this.recalculatePosition(interval.start);
      interval.end = this.recalculatePosition(interval.end);

      const errorMessage = unEscapeString(diagnostic.message);
      const severity = diagnostic.severity;
      const containsSuggestions =
        !!diagnostic.imports && diagnostic.imports.length !== 0;

      this.arrayClasses.push(
        this.codemirror.markText(interval.start, interval.end, {
          className: 'cm__' + diagnostic.className,
          title: errorMessage,
        }),
      );

      // contains suggestions => red underline
      if (containsSuggestions) {
        this.importsSuggestions.push({
          interval: interval,
          imports: diagnostic.imports,
        });
        this.arrayClasses.push(
          this.codemirror.markText(interval.start, interval.end, {
            className: 'cm__IMPORT',
          }),
        );
      }

      if (
        this.codemirror.lineInfo(interval.start.line) != null &&
        this.codemirror.lineInfo(interval.start.line).gutterMarkers == null
      ) {
        const gutter = document.createElement('div');
        gutter.className = severity + SELECTORS.GUTTER;
        gutter.setAttribute(SELECTORS.LABEL, errorMessage);
        this.codemirror.setGutterMarker(
          interval.start.line,
          SELECTORS.ERROR_AND_WARNING_GUTTER,
          gutter,
        );
      } else {
        const gutter = this.codemirror.lineInfo(interval.start.line)
          .gutterMarkers[SELECTORS.ERROR_AND_WARNING_GUTTER];
        gutter.setAttribute(
          SELECTORS.LABEL,
          gutter.getAttribute(SELECTORS.LABEL) + `\n${errorMessage}`,
        );
        if (gutter.className.indexOf(SELECTORS.ERROR_GUTTER) === -1) {
          gutter.className = severity + SELECTORS.GUTTER;
        }
      }
    });
  }

  removeStyles() {
    this.arrayClasses.forEach((it) => it.clear());
    this.codemirror.clearGutter(SELECTORS.ERROR_AND_WARNING_GUTTER);
    this.importsSuggestions = [];
  }

  initializeCodeMirror(options = {}) {
    const textarea = this.nodes[0].getElementsByTagName('textarea')[0];
    const readOnly = options.highlightOnly;
    
    // Apply critical styles to textarea immediately to prevent FOUC
    if (textarea && !readOnly) {
      textarea.style.fontFamily = 'menlo, consolas, monospace';
      textarea.style.fontSize = '12px';
      textarea.style.lineHeight = '1.4';
    }
    const codemirrorOptions = {
      readOnly: readOnly,
      lineNumbers: false,
      mode: options.mode,
      theme: options.theme,
      matchBrackets: options.matchBrackets,
      scrollbarStyle: options.scrollbarStyle || 'overlay',
      continueComments: true,
      autoCloseBrackets: true,
      indentUnit: options.indent,
      viewportMargin: Infinity,
      foldGutter: true,
      gutters: [SELECTORS.ERROR_AND_WARNING_GUTTER, SELECTORS.FOLD_GUTTER],
    };

    // Workaround to allow copy code in read-only mode
    // Taken from https://github.com/codemirror/CodeMirror/issues/2568#issuecomment-308137063
    if (readOnly) {
      codemirrorOptions.cursorBlinkRate = -1;
    }

    this.codemirror = CodeMirror.fromTextArea(textarea, codemirrorOptions);
    
    // Remove loading class once CodeMirror is initialized
    const wrapperElement = this.nodes[0].parentElement;
    if (wrapperElement && wrapperElement.classList.contains('kotlin-code-loading')) {
      // Use a small timeout to ensure CodeMirror has fully rendered and styles are applied
      setTimeout(() => {
        wrapperElement.classList.remove('kotlin-code-loading');
      }, 100);
    }
    
    // Also remove loading class from the original kotlin-code element
    const kotlinCodeElements = document.querySelectorAll('.kotlin-code.kotlin-code-loading');
    kotlinCodeElements.forEach(element => {
      if (element.getAttribute('data-kotlin-playground-initialized') === 'true') {
        setTimeout(() => {
          element.classList.remove('kotlin-code-loading');
        }, 100);
      }
    });

    // don't need to create additional editor options in readonly mode.
    if (readOnly) return;

    // Track if the user has actually interacted with the editor (clicked/focused)
    this._userHasInteracted = false;

    // Setup subtle highlight status UI (chip + optional gutter dot)
    this._pendingTimer = null;
    this._countdownTimer = null;
    this._inflight = false;
    this._debounceDueAt = 0;
    const IDLE_BEFORE_UI = 400; // ms

    // Create status chip and append to the editor container
    try {
      const container = this.nodes[0].getElementsByClassName('executable-fragment')[0] || this.nodes[0];
      this.statusChip = document.createElement('div');
      const themeClass = ' darcula'; // assume it's a dark theme, can play with this later
      this.statusChip.className = 'kp-status-chip' + themeClass;
      this.statusChip.setAttribute('aria-live', 'polite');
      this.statusChip.setAttribute('aria-atomic', 'true');
      this.statusChip.innerHTML = '<span class="kp-status-text"><span class="kp-status-glyph" style="padding-top: 3px" aria-hidden="true">⏳︎</span><span class="kp-status-label">Checking…</span></span>';
      container && container.appendChild(this.statusChip);
    } catch (e) {
      // no-op: if container not found, skip indicator
    }

    const showChip = (text) => {
      if (!this.statusChip) return;
      const labelNode = this.statusChip.querySelector('.kp-status-label');
      const finalText = text || 'Checking…';
      if (labelNode) labelNode.textContent = finalText;
      // Toggle progress state: show orb only while checking/counting down
      if (typeof finalText === 'string' && finalText.startsWith('Checking')) {
        this.statusChip.classList.add('kp-status-chip--progress');
      } else {
        this.statusChip.classList.remove('kp-status-chip--progress');
      }
      this.statusChip.classList.add('kp-status-chip--visible');
    };

    const hideChip = () => {
      if (!this.statusChip) return;
      this.statusChip.classList.remove('kp-status-chip--visible');
      this.statusChip.classList.remove('kp-status-chip--progress');
    };

    const makePendingGutterMarker = () => {
      const m = document.createElement('div');
      m.className = 'kp-gutter-pending';
      return m;
    };

    const startCountdown = () => {
      if (!this.statusChip) return;
      if (DEBOUNCE_TIME < 3000) { showChip('Checking…'); return; }
      const update = () => {
        const remaining = Math.max(0, this._debounceDueAt - Date.now());
        // Show whole seconds only; avoid flickering "0 s" right before firing
        if (remaining <= 0 || this._inflight === true) { clearInterval(this._countdownTimer); return; }
        const seconds = Math.ceil(remaining / 1000);
        showChip(`Checking in ${seconds}s`);
      };
      clearInterval(this._countdownTimer);
      this._countdownTimer = setInterval(update, 100);
      update();
    };

    // const scheduleChip = () => {
    //   clearTimeout(this._pendingTimer);
    //   this._pendingTimer = setTimeout(() => {
    //     startCountdown();
    //     try {
    //       this.codemirror.setGutterMarker(0, SELECTORS.ERROR_AND_WARNING_GUTTER, makePendingGutterMarker());
    //     } catch (e) {}
    //   }, IDLE_BEFORE_UI);
    // };

    // const clearPendingUI = () => {
    //   console.log('------- Clearing Pending UI -------');
    //   clearTimeout(this._pendingTimer);
    //   clearInterval(this._countdownTimer);
    //   hideChip();
    //   try { this.codemirror && this.codemirror.clearGutter(SELECTORS.ERROR_AND_WARNING_GUTTER); } catch (e) {}
    // };

    const scheduleChip = () => {
      clearTimeout(this._pendingTimer);
      this._pendingTimer = setTimeout(() => {
        startCountdown();
        try {
          this._pendingGutterLine = 0;
          this.codemirror.setGutterMarker(0, SELECTORS.ERROR_AND_WARNING_GUTTER, makePendingGutterMarker());
        } catch (e) {}
      }, IDLE_BEFORE_UI);
    };

    const clearPendingUI = () => {
      clearTimeout(this._pendingTimer);
      clearInterval(this._countdownTimer);
      hideChip();
      // Only clear the pending marker, not actual error markers
      try {
        if (this.codemirror && this._pendingGutterLine !== undefined) {
          this.codemirror.setGutterMarker(this._pendingGutterLine, SELECTORS.ERROR_AND_WARNING_GUTTER, null);
          this._pendingGutterLine = undefined;
        }
      } catch (e) {}
    };

    /**
     * Show highlight for extraKey Ctrl+Alt+H/Cmd+Option+H
     */
    let highlight = () => {
      const { compilerVersion, targetPlatform, hiddenDependencies } =
        this.state;
      this.removeStyles();
      WebDemoApi.getHighlight(
        this.getCode(),
        compilerVersion,
        targetPlatform,
        hiddenDependencies,
      ).then((data) => this.showDiagnostics(data));
    };

    // Setup autocomplete for Kotlin
    const { loadingIndicator, showImportSuggestions } = setupAutocomplete(this.codemirror, this);
    
    // Store reference for cleanup
    const clearAutocompleteLoading = () => loadingIndicator.clear();

    if (window.navigator.appVersion.indexOf(MAC) !== -1) {
      this.codemirror.setOption('extraKeys', {
        'Cmd-Alt-L': 'indentAuto',
        'Shift-Tab': 'indentLess',
        'Ctrl-/': 'toggleComment',
        'Cmd-[': false,
        'Cmd-]': false,
        'Ctrl-Space': 'autocomplete',
        'Cmd-Alt-H': highlight,
        'Cmd-Alt-Enter': debounce(showImportSuggestions, DEBOUNCE_TIME),
      });
    } else {
      this.codemirror.setOption('extraKeys', {
        'Ctrl-Alt-L': 'indentAuto',
        'Shift-Tab': 'indentLess',
        'Ctrl-/': 'toggleComment',
        'Ctrl-[': false,
        'Ctrl-]': false,
        'Ctrl-Space': 'autocomplete',
        'Ctrl-Alt-H': highlight,
        'Ctrl-Alt-Enter': debounce(showImportSuggestions, DEBOUNCE_TIME),
      });
    }

    /**
     * When editor's changed:
     * 1) Remove all styles
     * 2) if onFlyHighLight flag => getting highlight
     * Also: manage subtle status indicator lifecycle.
     */
    // On every raw change (no debounce) – schedule subtle UI and compute due time
    this.codemirror.on('change', () => {
      this._debounceDueAt = Date.now() + DEBOUNCE_TIME;
      this._inflight = false;
      if (this._userHasInteracted) {
        // This starts the countdown timer to the status chip i.e. the visual indicator that code is going to be highlight checked
        scheduleChip();
      }
    });

    // Existing debounced change handler with status chip
    this.codemirror.on(
      'change',
      debounce((cm) => {
        const {
          onChange,
          onFlyHighLight,
          compilerVersion,
          targetPlatform,
          hiddenDependencies,
        } = this.state;

        // Mark that the user has made a change, but only if they've actually interacted
        // with the editor (clicked/focused) and the cursor is present
        if (this._userHasInteracted && cm.hasFocus()) {
          this.state.changedAtLeastOnce = true;
        }

        if (onChange) onChange(cm.getValue());
        this.removeStyles();

        // Only call getHighlight if the user has actually made a change
        if (onFlyHighLight && this.state.changedAtLeastOnce) {
          clearInterval(this._countdownTimer);
          this._inflight = true;
          showChip('Checking…');
          WebDemoApi.getHighlight(
            this.getCode(),
            compilerVersion,
            targetPlatform,
            hiddenDependencies,
          ).then((data) => {
            this.showDiagnostics(data);
            const errors = (data && data.errors && data.errors.length) || 0;
            const warnings = (data && data.warnings && data.warnings.length) || 0;
            const summary = errors + warnings === 0
              ? 'Done'
              : `${errors ? errors + ' error' + (errors>1?'s':'') : ''}${errors && warnings ? ', ' : ''}${warnings ? warnings + ' warning' + (warnings>1?'s':'') : ''}`;
            showChip(summary);
            setTimeout(() => { clearPendingUI(); }, 1600);
          }).catch(() => {
            showChip('Check failed');
            setTimeout(() => { clearPendingUI(); }, 1600);
          });
        } else {
          clearPendingUI();
        }
      }, DEBOUNCE_TIME),
    );

    // If the user types again while pending or in-flight – clear UI immediately
    this.codemirror.on('keydown', () => { 
      clearPendingUI();
      clearAutocompleteLoading();
    });

    /**
     * Clear autocomplete loading indicator on cursor activity (movement via keyboard or mouse)
     */
    this.codemirror.on('cursorActivity', () => {
      // Only clear if cursor has actually moved from the loading position
      const loadingPos = loadingIndicator.getPosition();
      if (loadingPos) {
        const currentPos = this.codemirror.getCursor();
        if (currentPos.line !== loadingPos.line || 
            currentPos.ch !== loadingPos.ch) {
          clearAutocompleteLoading();
        }
      }
    });

    /**
     * If autoComplete => Getting completion on every key press on the editor.
     */
    this.codemirror.on(
      'keypress',
      debounce((cm, event) => {
        if (event.keyCode !== KEY_CODES.R && !event.ctrlKey) {
          if (this.state.autoComplete && !cm.state.completionActive) {
            CodeMirror.showHint(cm, CodeMirror.hint.kotlin, {
              completeSingle: false,
            });
          }
        }
      }, DEBOUNCE_TIME),
    );

    /**
     * Select marker's placeholder on mouse click
     */
    this.codemirror.on('mousedown', (codemirror, event) => {
      clearAutocompleteLoading();
      let position = codemirror.coordsChar({
        left: event.pageX,
        top: event.pageY,
      });
      if (position.line !== 0 || position.ch !== 0) {
        let markers = codemirror.findMarksAt(position);
        let todoMarker = markers.find(
          (marker) => marker.className === SELECTORS.MARK_PLACEHOLDER,
        );
        if (todoMarker != null) {
          let markerPosition = todoMarker.find();
          codemirror.setSelection(markerPosition.from, markerPosition.to);
          codemirror.focus();
          event.preventDefault();
        }
      }
    });

    /**
     * Track when the user actually interacts with the editor
     * Set the flag on focus or mousedown to ensure we only call getHighlight
     * after real user interaction, not during initialization
     */
    this.codemirror.on('focus', () => {
      this._userHasInteracted = true;
    });

    this.codemirror.on('mousedown', () => {
      this._userHasInteracted = true;
      clearAutocompleteLoading();
    });
  }

  destroy() {
    this.canAddImport = false;
    this.importsSuggestions = [];
    this.arrayClasses = null;
    this.initialized = false;
    this.jsExecutor = false;
    if (this.schemaFragment) {
      this.schemaFragment.destroy();
      this.schemaFragment = null;
    }
    this.state = null;
    if (this.codemirror && this.codemirror.toTextArea) {
      this.codemirror.toTextArea();
    }
    // Only call off() if there are registered event listeners
    if (this.__events__ && Object.keys(this.__events__).length > 0) {
      this.off();
    }
    this.remove();
  }
}
